import { FC, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useQuery } from '@apollo/client'
import { Alert, Badge, Button, Empty, Spin, Table, Tag, theme, Tooltip } from 'antd'
import {
  ArrowLeftOutlined,
  CloseOutlined,
  DoubleLeftOutlined,
  DoubleRightOutlined,
  ExclamationCircleFilled,
  LoadingOutlined,
  ShoppingCartOutlined,
} from '@ant-design/icons'
import classNames from 'classnames'
import Enumerable from 'linq'
import { translate, translatePlural } from '../../../core'
import { usdFormat } from '../../../core/until'
import { PurchaseOrderCalculationReviewQuery } from '../graphql/purchase-order.graphql'
import { PurchaseOrderCalculationRequest, PurchaseOrderCalculationReviewResponse } from '../type/purchase-order'
import { hasCalculationSources, PurchaseOrderCartLine, PurchaseOrderEntryContext } from './PurchaseOrderEntryContext'
import { sourceTagMap } from './PurchaseOrderEntryComponent'
import styles from './PurchaseOrderEntry.module.scss'

export type PurchaseOrderCart = {
  groups: PurchaseOrderCalculationReviewResponse[]
  total: number
  countBySource: Record<PurchaseOrderCartLine['source'], number>
  refreshing: boolean
  failed: boolean
}

const CART_REFRESH_DELAY_MS = 300

const CART_COLLAPSED_STORAGE_KEY = 'purchaseOrderEntryCartCollapsed'

const readCartCollapsed = () => {
  try {
    return localStorage.getItem(CART_COLLAPSED_STORAGE_KEY) === 'true'
  } catch {
    return false
  }
}

export const usePurchaseOrderCart = (request: PurchaseOrderCalculationRequest): PurchaseOrderCart => {
  const requestKey = JSON.stringify(request)
  const [settledKey, setSettledKey] = useState(requestKey)

  useEffect(() => {
    const timer = setTimeout(() => setSettledKey(requestKey), CART_REFRESH_DELAY_MS)
    return () => clearTimeout(timer)
  }, [requestKey])

  const settledRequest = useMemo<PurchaseOrderCalculationRequest>(() => JSON.parse(settledKey), [settledKey])
  const hasSources = hasCalculationSources(settledRequest)

  const { data, previousData, loading, error } = useQuery(PurchaseOrderCalculationReviewQuery, {
    variables: { request: settledRequest },
    skip: !hasSources,
    errorPolicy: 'all',
  })

  return useMemo(() => {
    const groups = hasSources
      ? (data ?? previousData)?.purchaseOrderCalculationReview ?? []
      : []
    const lines = groups.flatMap(group => group.items)
    const countSource = (source: PurchaseOrderCartLine['source']) => lines.filter(line => line.source === source).length

    return {
      groups,
      total: Enumerable.from(groups).sum(group => group.total),
      countBySource: {
        BackOrder: countSource('BackOrder'),
        CustomSku: countSource('CustomSku'),
        MinMax: countSource('MinMax'),
      },
      refreshing: requestKey !== settledKey || (hasSources && loading),
      failed: hasSources && !!error,
    }
  }, [data, previousData, hasSources, loading, error, requestKey, settledKey])
}

export const PurchaseOrderCartSourceTag: FC<{ source: PurchaseOrderCartLine['source'] }> = ({ source }) => (
  <Tag bordered={false} color={sourceTagMap[source].color} className={styles.sourceTag}>
    {sourceTagMap[source].display}
  </Tag>
)

const cartLineKey = (line: PurchaseOrderCartLine) => `${line.source}-${line.item.id}`

type PurchaseOrderEntryCartProps = {
  cart: PurchaseOrderCart
  reviewing: boolean
  onReview: () => void
  onBackToEditing: () => void
  onGenerate: () => void
}

export const PurchaseOrderEntryCart: FC<PurchaseOrderEntryCartProps> = ({
  cart,
  reviewing,
  onReview,
  onBackToEditing,
  onGenerate,
}) => {
  const { token } = theme.useToken()
  const { removeCartLine } = useContext(PurchaseOrderEntryContext)
  const [collapsed, setCollapsed] = useState(readCartCollapsed)

  const railRef = useRef<HTMLButtonElement>(null)
  const hideButtonRef = useRef<HTMLButtonElement | HTMLAnchorElement>(null)
  const focusAfterToggle = useRef(false)

  useEffect(() => {
    if (!focusAfterToggle.current)
      return

    focusAfterToggle.current = false
    const target = collapsed ? railRef.current : hideButtonRef.current
    target?.focus()
  }, [collapsed])

  const purchaseOrderCount = cart.groups.length
  const lineCount = cart.countBySource.BackOrder + cart.countBySource.CustomSku + cart.countBySource.MinMax
  const canGenerate = purchaseOrderCount > 0 && !cart.refreshing && !cart.failed

  const changeCollapsed = (next: boolean) => {
    focusAfterToggle.current = true
    setCollapsed(next)
    try {
      localStorage.setItem(CART_COLLAPSED_STORAGE_KEY, String(next))
    } catch {
      // Storage blocked: the choice just won't be remembered.
    }
  }

  const refreshFailed = cart.failed && (
    <Alert type='error' showIcon className='mb-3' message={translate('po_cart_refresh_failed')} />
  )

  if (reviewing)
    return (
      <section className={styles.review}>
        <div className={styles.reviewHead}>
          <div>
            <h2 className={styles.reviewTitle}>
              {translatePlural(purchaseOrderCount, 'po_review_title_one', 'po_review_title_many')}
            </h2>
            <div className={styles.reviewSubtitle}>{translate('po_review_subtitle')}</div>
          </div>
          <Button icon={<ArrowLeftOutlined />} onClick={onBackToEditing}>
            {translate('back_to_editing')}
          </Button>
        </div>

        {refreshFailed}

        <Spin spinning={cart.refreshing}>
          {purchaseOrderCount
            ? cart.groups.map(group => (
              <div key={group.vendor.id} className={styles.reviewGroup}>
                <div className={styles.reviewGroupHead}>
                  <span>{group.vendor.contactId}</span>
                  <span>{usdFormat(group.total)}</span>
                </div>
                <Table<PurchaseOrderCartLine>
                  size='small'
                  scroll={{ x: true }}
                  pagination={false}
                  rowKey={cartLineKey}
                  dataSource={group.items}
                  columns={[
                    { key: 'sku', title: translate('sku'), render: (_, line) => line.item.sku },
                    { key: 'description', title: translate('description'), render: (_, line) => line.item.description },
                    { dataIndex: 'quantity', title: translate('quantity'), align: 'right' },
                    { dataIndex: 'price', title: translate('unit_cost'), align: 'right', render: (price: number) => usdFormat(price) },
                    {
                      key: 'total',
                      title: translate('total'),
                      align: 'right',
                      render: (_, line) => usdFormat(line.price * line.quantity),
                    },
                    {
                      dataIndex: 'source',
                      title: translate('source'),
                      render: (source: PurchaseOrderCartLine['source']) => <PurchaseOrderCartSourceTag source={source} />,
                    },
                  ]}
                />
              </div>
            ))
            : <Empty description={translate('po_cart_empty')} />}
        </Spin>

        <div className={styles.reviewTotal}>
          <span>{translate('total')}</span>
          <span>{usdFormat(cart.total)}</span>
        </div>

        <div className={styles.reviewFooter}>
          <Button onClick={onBackToEditing}>{translate('back_to_editing')}</Button>
          <Button type='primary' disabled={!canGenerate} onClick={onGenerate}>
            {translatePlural(purchaseOrderCount, 'generate_purchase_orders_one', 'generate_purchase_orders_many')}
          </Button>
        </div>
      </section>
    )

  if (collapsed)
    return (
      <aside className={classNames(styles.cart, styles.cartCollapsed)}>
        <button
          ref={railRef}
          type='button'
          className={styles.cartRail}
          title={translate('show_po_cart')}
          aria-label={translate('show_po_cart')}
          aria-expanded={false}
          onClick={() => changeCollapsed(false)}
        >
          <DoubleLeftOutlined />
          <Badge count={lineCount} size='small' color='blue'>
            <ShoppingCartOutlined className={styles.cartRailIcon} />
          </Badge>
          {cart.failed && <ExclamationCircleFilled style={{ color: token.colorError }} />}
          {cart.refreshing && <LoadingOutlined />}
          <span className={styles.cartRailLabel}>
            {translate('po_cart')}
            {purchaseOrderCount > 0 && ` · ${usdFormat(cart.total)}`}
          </span>
        </button>
      </aside>
    )

  return (
    <aside className={styles.cart}>
      <div className={styles.cartHead}>
        <div className={styles.cartTitle}>
          <h3>{translate('po_cart')}</h3>
          <span className={styles.cartTitleActions}>
            {cart.refreshing && <Spin size='small' />}
            <Tooltip title={translate('hide_po_cart')}>
              <Button
                ref={hideButtonRef}
                type='text'
                size='small'
                icon={<DoubleRightOutlined />}
                aria-label={translate('hide_po_cart')}
                aria-expanded
                onClick={() => changeCollapsed(true)}
              />
            </Tooltip>
          </span>
        </div>
        <div className={styles.vendorSummary}>
          {purchaseOrderCount
            ? cart.groups.map(group => (
              <span key={group.vendor.id} className={styles.vendorChip}>
                {group.vendor.contactId} · {group.items.length}
              </span>
            ))
            : <span className={styles.vendorChip}>{translate('po_cart_no_items')}</span>}
        </div>
      </div>

      <div className={styles.cartList}>
        {refreshFailed}

        {purchaseOrderCount
          ? cart.groups.map(group => (
            <section key={group.vendor.id} className={styles.cartGroup}>
              <div className={styles.cartGroupTitle}>
                <span>{group.vendor.contactId}</span>
                <span>{usdFormat(group.total)}</span>
              </div>
              {group.items.map(line => (
                <div key={cartLineKey(line)} className={styles.cartLine}>
                  <div className={styles.cartLineText}>
                    <div>{line.item.description}</div>
                    <div className={styles.cartLineMeta}>
                      {line.item.sku} · {translate('qty_abbr')} {line.quantity} · <PurchaseOrderCartSourceTag source={line.source} />
                    </div>
                  </div>
                  <Tooltip title={translate('remove')}>
                    <Button
                      type='text'
                      size='small'
                      icon={<CloseOutlined />}
                      aria-label={translate('remove')}
                      onClick={() => removeCartLine(line)}
                    />
                  </Tooltip>
                </div>
              ))}
            </section>
          ))
          : <div className={styles.cartEmpty}>{translate('po_cart_empty')}</div>}
      </div>

      <div className={styles.cartFooter}>
        {purchaseOrderCount > 0 && (
          <>
            <div className={styles.cartTotal}>
              <span>{translate('total')}</span>
              <strong>{usdFormat(cart.total)}</strong>
            </div>
            <div className={styles.cartNote}>
              {translatePlural(purchaseOrderCount, 'po_cart_will_generate_one', 'po_cart_will_generate_many')}
            </div>
          </>
        )}
        <Button type='primary' block disabled={!canGenerate} onClick={onReview}>
          {translate('review_and_generate')}
        </Button>
        <Button block className='mt-2' disabled={!canGenerate} onClick={onGenerate}>
          {translate('generate_without_review')}
        </Button>
      </div>
    </aside>
  )
}
